/**
 * AI Recruit - Upload History Panel (Candidates page)
 *
 * A recruiter's own upload activity for the last few days, so they don't
 * need to keep the bulk-upload popup open to find out what happened to a
 * file (see BulkUploadResumesModal — it now closes as soon as files are
 * inserted into the database, without waiting for AI analysis to finish).
 * This panel is where that Queued → Processing → Analyzed/Failed
 * progression actually stays visible afterward.
 *
 * Merges two different kinds of record from GET /resumes/my-upload-history:
 * - Files rejected before they ever became a resume row (bad format,
 *   oversized, ...) — no resume_id, so the only fix is a fresh re-upload.
 * - Resumes that were inserted, at any stage since (still queued, being
 *   analyzed, or finished) — has a resume_id, so a failure here can offer
 *   Reanalyze (the same action, and the same visible label, as the
 *   Candidate list's own row-level Reanalyze button).
 */
import React, { useState, useRef, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-hot-toast'
import { BellIcon, ClockIcon, ArrowPathIcon } from '@heroicons/react/24/outline'
import { CheckCircleIcon, ExclamationCircleIcon } from '@heroicons/react/24/solid'
import { resumesApi } from '@/services/api'
import { formatToCompactDateTime } from '../../utils/dateUtils'
import LoadingSpinner from '@/components/common/LoadingSpinner'
import { useAuth } from '@/hooks/useAuth'

type StatusLabel = 'Queued' | 'Processing' | 'Analyzed' | 'Failed'

interface UploadHistoryItem {
  id: string
  original_filename: string
  status_label: StatusLabel
  reason?: string | null
  timestamp: string
  resume_id?: string | null
  candidate_id?: string | null
}

interface UploadHistoryResponse {
  items: UploadHistoryItem[]
  total_queued: number
  total_processing: number
  total_analyzed: number
  total_failed: number
  days: number
}

const HISTORY_DAYS = 3

// 'upload_failed': rejected before it ever became a resume row (no
// resume_id) -- the only fix is a fresh re-upload of a corrected file.
// 'analysis_failed': the resume WAS inserted and only failed later, during
// AI analysis (has a resume_id) -- Reanalyze retries that same record.
// Kept as separate tabs since the two need genuinely different actions.
type HistoryTab = 'all' | 'queued' | 'processing' | 'analyzed' | 'upload_failed' | 'analysis_failed'

const UploadHistoryPanel: React.FC = () => {
  const { user } = useAuth()
  const [open, setOpen] = useState(false)
  const [tab, setTab] = useState<HistoryTab>('all')
  const containerRef = useRef<HTMLDivElement>(null)
  const queryClient = useQueryClient()

  // "Read" state for the badge, standard notification-bell behavior: opening
  // the panel clears it, and only failures that happened AFTER that point
  // count again. Scoped per user (localStorage) so a shared machine doesn't
  // leak one recruiter's read state to another; per-viewer only, never
  // something the badge count on a fresh browser/device needs to agree with.
  const seenStorageKey = user?.id ? `upload-history-last-seen:${user.id}` : null
  const [lastSeenAt, setLastSeenAt] = useState<string | null>(null)

  // Re-read once the user id is actually known -- useAuth resolves it async,
  // so the key above is often null on the very first render.
  useEffect(() => {
    if (!seenStorageKey) return
    try {
      setLastSeenAt(localStorage.getItem(seenStorageKey))
    } catch {
      // ignore -- private browsing / storage blocked
    }
  }, [seenStorageKey])

  const historyQuery = useQuery({
    queryKey: ['my-upload-history', HISTORY_DAYS],
    queryFn: async () => (await resumesApi.getMyUploadHistory(HISTORY_DAYS)).data as UploadHistoryResponse,
    // Always enabled, not just while open -- a badge that only populates
    // after you've already clicked the bell can never proactively alert
    // you to anything, which defeats the point of a badge.
    staleTime: 30 * 1000,
    refetchInterval: (query) => {
      const d = query.state.data as UploadHistoryResponse | undefined
      const hasLiveActivity = d && d.total_queued + d.total_processing > 0
      // Poll quickly while the panel's open and something's genuinely in
      // flight; otherwise just keep the badge reasonably fresh in the
      // background without hammering the endpoint on every page view.
      if (open) return hasLiveActivity ? 5000 : false
      return 60000
    },
  })

  const reprocessMutation = useMutation({
    mutationFn: (resumeId: string) => resumesApi.reprocess(resumeId),
    onSuccess: () => {
      toast.success('Resume reprocessing started')
      queryClient.invalidateQueries({ queryKey: ['resumes'] })
      // This panel reads its OWN query (my-upload-history), separate from the
      // candidate table's ['resumes'] above -- without this it kept showing
      // "Reanalyze" on the stale Failed item even though reprocessing had
      // already started and the candidate table had moved on to Processing.
      queryClient.invalidateQueries({ queryKey: ['my-upload-history'] })
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Reprocess failed')
    },
  })

  useEffect(() => {
    if (!open) return
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [open])

  useEffect(() => {
    if (!open) setTab('all')
  }, [open])

  // Mark everything as read the moment the bell is opened -- standard
  // notification-bell behavior (Gmail, Slack, GitHub): viewing clears the
  // badge, it doesn't require acting on each item. A failure that arrives
  // AFTER this point (timestamp > lastSeenAt) will count again next time.
  useEffect(() => {
    if (!open || !seenStorageKey) return
    const now = new Date().toISOString()
    setLastSeenAt(now)
    try {
      localStorage.setItem(seenStorageKey, now)
    } catch {
      // ignore -- private browsing / storage blocked
    }
  }, [open, seenStorageKey])

  const items = historyQuery.data?.items ?? []
  const queuedCount = historyQuery.data?.total_queued ?? 0
  const processingCount = historyQuery.data?.total_processing ?? 0
  const analyzedCount = historyQuery.data?.total_analyzed ?? 0
  // Split by whether a resume_id exists -- that's exactly what determines
  // which action (Reanalyze vs. re-upload) applies, so it's the right line
  // to split the "Failed" bucket on.
  const uploadFailedCount = items.filter((item) => item.status_label === 'Failed' && !item.resume_id).length
  const analysisFailedCount = items.filter((item) => item.status_label === 'Failed' && !!item.resume_id).length
  // Badge count: only failures the user hasn't already seen (opened the
  // bell since), across both kinds. The tab counts above stay as real
  // totals -- only the badge is "unread."
  const unseenFailedCount = items.filter(
    (item) => item.status_label === 'Failed' && (!lastSeenAt || item.timestamp > lastSeenAt)
  ).length
  const visibleItems = items.filter((item) => {
    if (tab === 'queued') return item.status_label === 'Queued'
    if (tab === 'processing') return item.status_label === 'Processing'
    if (tab === 'analyzed') return item.status_label === 'Analyzed'
    if (tab === 'upload_failed') return item.status_label === 'Failed' && !item.resume_id
    if (tab === 'analysis_failed') return item.status_label === 'Failed' && !!item.resume_id
    return true
  })

  const TABS: { key: HistoryTab; label: string; count: number }[] = [
    { key: 'all', label: 'All', count: items.length },
    { key: 'queued', label: 'Queued', count: queuedCount },
    { key: 'processing', label: 'Processing', count: processingCount },
    { key: 'analyzed', label: 'Completed', count: analyzedCount },
    { key: 'upload_failed', label: 'Upload Failed', count: uploadFailedCount },
    { key: 'analysis_failed', label: 'Analysis Failed', count: analysisFailedCount },
  ]

  return (
    <div className="relative" ref={containerRef}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="relative p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
        title="Your upload activity from the last few days"
        aria-label="Upload history"
      >
        <BellIcon className="w-5 h-5" />
        {unseenFailedCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 inline-flex items-center justify-center h-4 min-w-[1rem] px-1 rounded-full bg-red-500 text-white text-[10px] font-semibold leading-none">
            {unseenFailedCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-96 max-h-[28rem] flex flex-col bg-white border border-gray-200 rounded-lg shadow-lg z-50">
          <div className="px-4 py-2.5 border-b border-gray-100 flex-shrink-0 flex items-center justify-between gap-2">
            <span className="text-sm font-semibold text-gray-800">Your uploads — last {HISTORY_DAYS} days</span>
            <button
              onClick={() => historyQuery.refetch()}
              disabled={historyQuery.isFetching}
              className="p-1 text-gray-400 hover:text-gray-700 rounded disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
              title="Refresh"
              aria-label="Refresh upload history"
            >
              <ArrowPathIcon className={`w-4 h-4 ${historyQuery.isFetching ? 'animate-spin' : ''}`} />
            </button>
          </div>

          <div className="px-3 py-2 border-b border-gray-100 flex-shrink-0 flex flex-wrap gap-1.5">
            {TABS.map((t) => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium border transition-colors ${
                  tab === t.key
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                }`}
              >
                <span className="font-semibold">{t.count}</span> {t.label}
              </button>
            ))}
          </div>

          <div className="overflow-y-auto flex-1 min-h-0">
            {historyQuery.isLoading && (
              <div className="px-4 py-6 text-sm text-gray-400 text-center">Loading…</div>
            )}

            {!historyQuery.isLoading && items.length === 0 && (
              <div className="px-4 py-6 text-sm text-gray-400 text-center">
                Nothing uploaded in the last {HISTORY_DAYS} days.
              </div>
            )}

            {!historyQuery.isLoading && items.length > 0 && visibleItems.length === 0 && (
              <div className="px-4 py-6 text-sm text-gray-400 text-center">
                Nothing in this filter.
              </div>
            )}

          <ul className="divide-y divide-gray-100">
            {visibleItems.map((item) => (
              <li key={item.id} className="px-4 py-3">
                <div className="flex items-start gap-2">
                  {item.status_label === 'Analyzed' ? (
                    <CheckCircleIcon className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                  ) : item.status_label === 'Failed' ? (
                    <ExclamationCircleIcon className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
                  ) : item.status_label === 'Processing' ? (
                    <span className="flex-shrink-0 mt-0.5"><LoadingSpinner size="sm" /></span>
                  ) : (
                    <ClockIcon className="h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5" />
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5 min-w-0">
                      {item.candidate_id && (
                        <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-800 rounded-full font-mono whitespace-nowrap flex-shrink-0">
                          {item.candidate_id}
                        </span>
                      )}
                      <p className="text-sm text-gray-800 truncate" title={item.original_filename}>
                        {item.original_filename}
                      </p>
                    </div>
                    <p className="text-xs text-gray-400">
                      {formatToCompactDateTime(item.timestamp)}
                      {(item.status_label === 'Queued' || item.status_label === 'Processing') && ` · ${item.status_label}`}
                    </p>
                    {item.status_label === 'Failed' && item.reason && (
                      <p className="text-xs text-amber-700 mt-1">{item.reason}</p>
                    )}
                    {item.status_label === 'Failed' && (
                      item.resume_id ? (
                        <button
                          onClick={() => reprocessMutation.mutate(item.resume_id!)}
                          disabled={reprocessMutation.isPending && reprocessMutation.variables === item.resume_id}
                          className="mt-1.5 text-xs font-medium text-blue-600 hover:text-blue-800 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {reprocessMutation.isPending && reprocessMutation.variables === item.resume_id
                            ? 'Reanalyzing…'
                            : 'Reanalyze'}
                        </button>
                      ) : (
                        <p className="mt-1 text-xs text-gray-400">Fix the file and upload it again</p>
                      )
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
          </div>
        </div>
      )}
    </div>
  )
}

export default UploadHistoryPanel
